import { useCallback, useState } from 'react';
import { AlertType, Checkbox, Typography, TypographyType } from '../../components';
import { useToast } from '../../hooks';
import { useUpdateOrganizationSettingsMutation } from '../../services';
import OrgTabSectionTitle from './OrgTabSectionTitle';

interface AccessSetting {
  label: string;
  id: keyof AccessProps;
}

const ACCESS_SETTINGS: readonly AccessSetting[] = [
  {
    id: 'repCanScoreSimCalls',
    label: 'Representatives can score practice calls',
  },
  {
    id: 'repCanScoreRealCalls',
    label: 'Representatives can score real calls',
  },
  {
    id: 'repCanCreateSims',
    label: 'Representatives can create simulations',
  },
  {
    id: 'repCanViewLibrary',
    label: 'Representatives can view the library',
  },
  {
    id: 'repCanEditLibrary',
    label: 'Representatives can edit the library',
  },
] as const;

interface AccessProps {
  repCanScoreSimCalls: boolean;
  repCanScoreRealCalls: boolean;
  repCanCreateSims: boolean;
  repCanViewLibrary: boolean;
  repCanEditLibrary: boolean;
}

type AccessSettings = Record<keyof AccessProps, boolean>;

const Access = (props: AccessProps) => {
  const [settings, setSettings] = useState<AccessSettings>(() =>
    ACCESS_SETTINGS.reduce(
      (acc, { id }) => ({
        ...acc,
        [id]: props[id],
      }),
      {} as AccessSettings
    )
  );
  const [error, setError] = useState(false);

  const [updateOrganizationSettings] = useUpdateOrganizationSettingsMutation();
  const { showToast } = useToast();

  const onError = useCallback(() => {
    showToast({ message: 'Failed to update organization settings', type: AlertType.ERROR });
    setError(true);
  }, [showToast]);

  const isCheckboxDisabled = useCallback(
    (settingId: keyof AccessSettings) => {
      if (settingId === 'repCanEditLibrary') {
        // disable edit library permission if any of its dependencies are false
        return !settings.repCanViewLibrary || !settings.repCanCreateSims;
      }
      return false;
    },
    [settings]
  );

  const handleUpdateSetting = useCallback(
    async (settingId: keyof AccessSettings, newValue: boolean) => {
      setError(false);

      // Automatically disable edit library permission if its dependencies are disabled
      const shouldDisableRepCanEditLibrary =
        (settingId === 'repCanViewLibrary' || settingId === 'repCanCreateSims') && !newValue;

      const repCanEditLibrary = shouldDisableRepCanEditLibrary ? false : settings.repCanEditLibrary;

      setSettings((prev) => ({
        ...prev,
        repCanEditLibrary,
        [settingId]: newValue,
      }));

      try {
        const response = await updateOrganizationSettings({
          repCanEditLibrary,
          [settingId]: newValue,
        });
        if (response.error) {
          onError();
        }
      } catch (error) {
        console.error(error);
        onError();
      }
    },
    [settings, updateOrganizationSettings, onError]
  );

  return (
    <div className="flex flex-col gap-4">
      <OrgTabSectionTitle title="Access" />
      {ACCESS_SETTINGS.map(({ id, label }) => (
        <div key={id} className="flex justify-between">
          <Typography type={TypographyType.BASE}>{label}</Typography>
          <Checkbox
            disabled={isCheckboxDisabled(id)}
            checked={settings[id]}
            setChecked={(newChecked) => handleUpdateSetting(id, newChecked)}
            error={error}
          />
        </div>
      ))}
    </div>
  );
};

export default Access;
